import React, { useState, useEffect } from "react";
import styled from "styled-components";

// import Page from "components/Page";
import RowCard from "./RowCard";
import {
  auditFirstLogins,
  normalizeUtcCutoffInputToMs,
} from "./firstLoginAudit";

import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  SG_GET_ACCOUNTS,
  UPDATE_ACCOUNT_PAGE_NUMBER,
  UPDATE_ACCOUNT_QUERY_PARAMS,
} from "constants/actions";

import {
  Dimmer,
  Loader,
  Card,
  Pagination,
  Segment,
  Icon,
  Input,
  Button,
  Message,
} from "semantic-ui-react";

const List = ({ orgId }) => {
  const dispatch = useDispatch();
  const [organizationId, setOrganizationId] = useState(orgId);
  const [resetPageLoad, setResetPageLoad] = useState(false);

  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  const [queryParams, setQueryParams] = useState([]);

  // Audit state (first login / UTC cutoff)
  const [cutoffInput, setCutoffInput] = useState("");
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditProgress, setAuditProgress] = useState({
    phase: "idle",
    page: 0,
    totalPages: 0,
    totalUsers: 0,
    userId: null,
    auditedCount: 0,
    matchedCount: 0,
    errorsCount: 0,
    delayMs: 1000,
  });
  const [auditError, setAuditError] = useState("");
  const [auditByUserId, setAuditByUserId] = useState({});
  const [auditMatches, setAuditMatches] = useState([]);
  const [showAuditMatchesOnly, setShowAuditMatchesOnly] = useState(false);
  const [auditController, setAuditController] = useState(null);

  const { get_accounts } = useSelector(
    (state) => ({
      get_accounts: state.account,
    }),
    shallowEqual
  );

  useEffect(() => {
    setOrganizationId(orgId);
  }, [orgId]);

  useEffect(() => {
    // Cleanup on unmount: stop any in-progress audit
    return () => {
      try {
        auditController?.abort?.();
      } catch (e) {
        // no-op
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilters(pageNo = false, orgId = false) {
    // setTimeout(() => {
    // if the response is ok 200
    let _queryParams = [];
    // Check if report status filter is present
    if (pageNo) {
      setPage(pageNo);
      _queryParams.push(`page=${pageNo}`);
    }
    if (orgId) {
      _queryParams.push(`organization_id=${orgId}`);
    }

    dispatch({
      type: UPDATE_ACCOUNT_QUERY_PARAMS,
      payload: _queryParams,
    });
    // }, 500);
  }

  useEffect(() => {
    if (queryParams && queryParams?.length > 0) {
      dispatch({
        type: SG_GET_ACCOUNTS,
        payload: queryParams.join("&"),
      });
    } else if (orgId) {
      dispatch({
        type: SG_GET_ACCOUNTS,
        payload: queryParams.join("&"),
      });
      dispatch({
        type: UPDATE_ACCOUNT_QUERY_PARAMS,
        payload: [`organization_id=${orgId}`],
      });
    } else {
      dispatch({
        type: SG_GET_ACCOUNTS,
      });
    }
  }, [dispatch, queryParams, orgId]);

  useEffect(() => {
    if (get_accounts?.queryParams) {
      setQueryParams(get_accounts?.queryParams);
    }
  }, [get_accounts]);

  useEffect(() => {
    setResults(get_accounts?.accounts?.results);
    setPage(get_accounts?.pageNumber);
    setTotalPages(
      Math.floor(
        get_accounts?.accounts?.count / get_accounts?.accounts?.page_size
      )
    );
    if (get_accounts?.accounts?.count % get_accounts?.accounts?.page_size) {
      setTotalPages(
        Math.floor(
          get_accounts?.accounts?.count / get_accounts?.accounts?.page_size
        )
      );
    }
  }, [get_accounts, page]);

  function handlePageChange(event, value) {
    applyFilters(value.activePage, organizationId);

    setPage(value.activePage);
    dispatch({
      type: UPDATE_ACCOUNT_PAGE_NUMBER,
      payload: value.activePage,
    });
  }

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setLoading(get_accounts?.pending);
    }, 2000);
    return () => clearTimeout(timer1);
  }, [get_accounts]);

  async function runAudit() {
    setAuditError("");

    const cutoffUtcMs = normalizeUtcCutoffInputToMs(cutoffInput);
    if (!Number.isFinite(cutoffUtcMs)) {
      setAuditError(
        'Invalid cutoff. Use either "YYYY-MM-DDTHH:mm" (your local time) or a full ISO string ending in "Z".'
      );
      return;
    }
    if (!organizationId) {
      setAuditError("Missing organization id (orgId).");
      return;
    }

    // Reset state for a new run
    setAuditByUserId({});
    setAuditMatches([]);
    setShowAuditMatchesOnly(true);
    setAuditProgress({
      phase: "starting",
      page: 0,
      totalPages: 0,
      totalUsers: 0,
      userId: null,
      auditedCount: 0,
      matchedCount: 0,
      errorsCount: 0,
    });

    const controller = new AbortController();
    setAuditController(controller);
    setAuditRunning(true);

    try {
      const res = await auditFirstLogins({
        orgId: organizationId,
        cutoffUtcMs,
        throttleMs: 1000,
        signal: controller.signal,
        onProgress: (patch) => {
          // Stream matches live
          if (patch?.phase === "match" && patch?.match?.user) {
            const m = patch.match;
            setAuditMatches((prev) => [...prev, m]);
            setAuditByUserId((prev) => ({
              ...prev,
              [m.user?.id]: m.firstLoginUtcMs,
            }));
          }
          setAuditProgress((prev) => ({ ...prev, ...patch }));
        },
      });

      setAuditByUserId(res.byUserId || {});
      setAuditMatches(res.matches || []);
      setAuditProgress((prev) => ({
        ...prev,
        phase: "done",
        auditedCount: res.auditedCount,
        matchedCount: res.matchedCount,
        errorsCount: (res.errors || []).length,
      }));
    } catch (e) {
      if (e?.name === "AbortError") {
        setAuditProgress((prev) => ({ ...prev, phase: "cancelled" }));
      } else {
        setAuditError(e?.message || String(e));
        setAuditProgress((prev) => ({ ...prev, phase: "error" }));
      }
    } finally {
      setAuditRunning(false);
      setAuditController(null);
    }
  }

  function stopAudit() {
    try {
      auditController?.abort?.();
    } catch (e) {
      // no-op
    }
  }

  const displayedResults = showAuditMatchesOnly
    ? auditMatches.map((m) => ({
        ...m.user,
        __firstLoginUtcMs: m.firstLoginUtcMs,
      }))
    : results?.map((u) => ({
        ...u,
        __firstLoginUtcMs: auditByUserId?.[u?.id] ?? null,
      }));

  return (
    <Container>
      <Segment id="results" basic>
        <SectionDiv>
          <AuditPanel>
            <AuditRow>
              <div>
                <strong>First login audit (UTC)</strong>
                <div style={{ opacity: 0.7, fontSize: 12 }}>
                  Cutoff is interpreted as <strong>your local time</strong> and
                  converted to UTC for comparison against API timestamps with
                  <code> Z</code>.
                </div>
                <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>
                  <strong>Viewing:</strong>{" "}
                  {showAuditMatchesOnly ? "Matches (across all pages)" : "Current page"}
                </div>
              </div>
            </AuditRow>

            <AuditRow>
              <Input
                type="datetime-local"
                value={cutoffInput}
                onChange={(e) => setCutoffInput(e.target.value)}
                placeholder="YYYY-MM-DDTHH:mm (local time)"
                disabled={auditRunning}
                style={{ minWidth: 260 }}
              />
              <Button
                primary
                onClick={runAudit}
                disabled={auditRunning || !organizationId}
              >
                Run audit
              </Button>
              <Button onClick={stopAudit} disabled={!auditRunning}>
                Stop
              </Button>
              <Button
                basic
                onClick={() => setShowAuditMatchesOnly((v) => !v)}
                disabled={auditRunning}
              >
                {showAuditMatchesOnly ? "Show current page" : "Show matches"}
              </Button>
            </AuditRow>

            {(auditError || auditProgress.phase !== "idle") && (
              <Message
                negative={!!auditError}
                info={!auditError}
                size="small"
              >
                {auditError ? (
                  <Message.Content>{auditError}</Message.Content>
                ) : (
                  <Message.Content>
                    <div>
                      <strong>Status:</strong> {auditProgress.phase}
                    </div>
                    <div>
                      <strong>Progress:</strong> {auditProgress.auditedCount}{" "}
                      audited, {auditProgress.matchedCount} matched,{" "}
                      {auditProgress.errorsCount} errors
                    </div>
                    <div>
                      <strong>Current delay:</strong>{" "}
                      {auditProgress.delayMs || 1000}ms{" "}
                      {auditProgress.phase === "backoff" ? "(backing off on 429)" : ""}
                    </div>
                    {auditProgress.totalPages ? (
                      <div>
                        <strong>Page:</strong> {auditProgress.page}/
                        {auditProgress.totalPages}
                        {auditProgress.userId ? (
                          <>
                            {" "}
                            — <strong>User:</strong> {auditProgress.userId}
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </Message.Content>
                )}
              </Message>
            )}
          </AuditPanel>
        </SectionDiv>

        {(auditRunning || auditMatches?.length > 0) && (
          <SectionDiv>
            <UiSubHeader>
              Live Matches (across all pages): {auditMatches?.length || 0}
            </UiSubHeader>
            <Card.Group>
              {auditMatches?.length > 0 ? (
                auditMatches.map((m) => (
                  <RowCard
                    key={m?.user?.id}
                    row={{
                      ...m.user,
                      __firstLoginUtcMs: m.firstLoginUtcMs,
                    }}
                    firstLoginUtcMs={m.firstLoginUtcMs}
                  />
                ))
              ) : (
                <p>No matches yet...</p>
              )}
            </Card.Group>
          </SectionDiv>
        )}

        <SectionDiv>
          <Pagination
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
            activePage={page || 1}
            totalPages={totalPages || 1}
            onPageChange={handlePageChange}
          />
        </SectionDiv>
        <Dimmer active={loading}>
          <Loader />
        </Dimmer>
        <Card.Group>
          {displayedResults?.length > 0 ? (
            displayedResults.map((result) => (
              <RowCard
                row={result}
                firstLoginUtcMs={result?.__firstLoginUtcMs}
                key={result?.id}
              />
            ))
          ) : showAuditMatchesOnly ? (
            <p>No matches found for the selected cutoff.</p>
          ) : (
            <p>No accounts found on this page.</p>
          )}
        </Card.Group>
        <SectionDiv>
          <Pagination
            ellipsisItem={null}
            firstItem={null}
            lastItem={null}
            activePage={page || 1}
            totalPages={totalPages || 1}
            onPageChange={handlePageChange}
          />
        </SectionDiv>
      </Segment>
    </Container>
  );
};

export default List;

const Container = styled.div`
  //   min-height: 90vh;
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionDiv = styled.div`
  padding: 30px 15px;
`;

const RightAlign = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ContainerFieldSelectable = styled.span`
  padding: 5px;
  margin: 5px 10px;
  background-color: ${(props) => (props.warn ? "black" : "#dcdcdc")};
  cursor: pointer;
`;

const WarningDiv = styled.span`
  color: red;
  font-weight: 800;
`;

const AuditPanel = styled.div`
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`;

const AuditRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const UiSubHeader = styled.div`
  font-weight: 700;
  margin: 0 0 10px 0;
`;
